import { useDisclosure } from '@mantine/hooks';
import { notifications as mantineNotification } from '@mantine/notifications';
import {
	type ActorDto,
	isApiError,
	TalkingHeadActorEventIdn
} from '@newo/api-client';
import { useCallback } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { PARTNER_INDUSTRY, USER_AGENT_VALUE } from '@/constants';
import { useHostContext } from '@/context/HostContext';
import { AgentIntroSourceType } from '@/pages/AgentIntro/components/TrainingSourceSelector/TrainingSourceSelector.type';
import { agentTrainingRealtime } from '@/services/AgentTrainingRealtime';
import type { StepperOnboardingFormValues } from '@/stepper/types';
import useAppStore from '@/store/app';
import { useActorSendEventMutation, useInitialize } from '@/utils/api';
import { buildTrainingStartPayload } from '@/utils/buildTrainingStartPayload';
import { isValid } from '@/utils/validation';
import useStepperQueryParams from './useStepperQueryParams';

type StepperPayloadContext = {
	memberIdn?: string | null;
	partnerIdn?: string | null;
	queryParams: ReturnType<typeof useStepperQueryParams>;
};

const buildStepperEventArguments = (
	formValues: StepperOnboardingFormValues,
	{ memberIdn, partnerIdn, queryParams }: StepperPayloadContext
) => [
	...buildTrainingStartPayload({
		source: formValues.websiteUrl.trim(),
		sourceType: AgentIntroSourceType.Website,
		paramsString: queryParams.queryParamsString,
		partner_idn: queryParams.queryPartnerIdn || partnerIdn,
		partner_member_idn: queryParams.queryPartnerMemberIdn || memberIdn,
		industry: formValues.industry,
		referral: queryParams.referral,
		fullName: formValues.managerFullName.trim(),
		email: formValues.managerEmail.trim(),
		phoneNumber: formValues.managerPhone.trim(),
		googleMapUrl: queryParams.googleMapUrl,
		language: queryParams.language,
		countryCode: queryParams.countryCode,
		externalAccountNumber: queryParams.externalAccountNumber,
		accountManagers: queryParams.accountManagers,
		technicalOwners: queryParams.technicalOwners,
		notifications: queryParams.notifications,
		comment: queryParams.comment,
		externalOwnerAccountId: queryParams.externalOwnerAccountId,
		hashExternalAccountId: queryParams.hashExternalAccountId,
		userAgent: USER_AGENT_VALUE
	}),
	{
		name: 'voice_greeting_phrase',
		value: formValues.greetingPhrase.trim()
	}
];

const buildStepperCustomerData = (
	formValues: StepperOnboardingFormValues,
	queryParams: ReturnType<typeof useStepperQueryParams>
) => {
	const fullName = formValues.managerFullName.trim();
	const email = formValues.managerEmail.trim();

	return {
		contact_fullname: isValid('contact_fullname', fullName) ? fullName : '',
		contact_email: isValid('contact_email', email) ? email : '',
		contact_phone: formValues.managerPhone.trim(),
		promo_code: queryParams.promoCode ?? '',
		industry: PARTNER_INDUSTRY || formValues.industry,
		country_code: queryParams.countryCode ?? ''
	};
};

const useStepperTraining = (actor?: ActorDto) => {
	const [isPending, { open: enableLoading, close: disableLoading }] =
		useDisclosure(false);
	const { localization, member_idn, partner_idn } = useHostContext();
	const stepperQueryParams = useStepperQueryParams();
	const { mutateAsync: initializeStepperApp } = useInitialize(true, actor);
	const { mutateAsync: sendActorEvent } = useActorSendEventMutation();
	const { clientSecret, connectorIdn, externalId, setCustomerData } =
		useAppStore(
			useShallow((state) => ({
				clientSecret: state.clientSecret,
				connectorIdn: state.connectorIdn,
				externalId: state.externalId,
				setCustomerData: state.setCustomerData
			}))
		);

	const resolveTrainingSettings = useCallback(async () => {
		const canUseStoredSettings =
			clientSecret &&
			connectorIdn &&
			externalId &&
			(!actor?.external_id || actor.external_id === externalId);

		if (canUseStoredSettings) {
			return { clientSecret, connectorIdn, externalId };
		}

		const initializedSettings = await initializeStepperApp(undefined);

		agentTrainingRealtime.init(initializedSettings);

		return initializedSettings;
	}, [
		actor?.external_id,
		clientSecret,
		connectorIdn,
		externalId,
		initializeStepperApp
	]);

	const submitOnboarding = useCallback(
		async (formValues: StepperOnboardingFormValues): Promise<boolean> => {
			const source = formValues.websiteUrl.trim();

			if (!source) {
				return false;
			}

			enableLoading();

			try {
				const settings = await resolveTrainingSettings();

				setCustomerData(
					buildStepperCustomerData(formValues, stepperQueryParams)
				);

				await sendActorEvent({
					client_secret: settings.clientSecret,
					connector_idn: settings.connectorIdn,
					event_idn: TalkingHeadActorEventIdn.OnboardingStarted,
					external_id: settings.externalId,
					arguments: buildStepperEventArguments(formValues, {
						memberIdn: member_idn,
						partnerIdn: partner_idn,
						queryParams: stepperQueryParams
					})
				});

				return true;
			} catch (error) {
				if (isApiError(error)) {
					mantineNotification.show({
						'data-testid': 'api-error-notification',
						color: 'red',
						message: localization.translate(
							'common',
							'something_wrong_select_location_again'
						),
						title: localization.translate('common', 'error')
					});
				}

				return false;
			} finally {
				disableLoading();
			}
		},
		[
			disableLoading,
			enableLoading,
			localization,
			member_idn,
			partner_idn,
			resolveTrainingSettings,
			sendActorEvent,
			setCustomerData,
			stepperQueryParams
		]
	);

	return {
		isPending,
		submitOnboarding
	};
};

export default useStepperTraining;
